// Rosary data — twenty mysteries and the full prayers.
import { BALTIMORE_SOURCE, TRADITIONAL_ENGLISH } from "./traditionalRosary";
// Scripture excerpts and "Fruit of the mystery" per the user's provided card set.
import { SPANISH_PRAYERS } from "./rosaryPrayers.es";
import { FATIMA_DECADE_PRAYER } from "./fatimaPrayers";
import { FRENCH_PRAYERS } from "./rosaryPrayers.fr";
import { localizeSet, mysteryLabel, rosaryUI } from "./rosaryLocale";
import { ROSARY_SCRIPTURE } from "./rosaryScripture";
import { PRAYER_EDITIONS } from "./prayerEditions";

const MYSTERY_SETS = {
  joyful: {
    id: "joyful",
    label: "Joyful Mysteries",
    day: "Monday · Saturday",
    kicker: "The Incarnation",
    color: "from-rose/30 to-white",
    intro: "The mysteries of the Word becoming flesh — from the Angel's greeting to the finding of the Child in the Temple.",
    mysteries: [
      {
        n: 1,
        title: "The Annunciation",
        verse: "And when the angel had come to her, he said, 'Hail, full of grace, the Lord is with thee. Blessed art thou among women.'",
        ref: "Luke 1:28",
        fruit: "Humility",
      },
      {
        n: 2,
        title: "The Visitation",
        verse: "Elizabeth was filled with the Holy Spirit and cried out in a loud voice: 'Blest are you among women and blest is the fruit of your womb.'",
        ref: "Luke 1:41–42",
        fruit: "Love of neighbour",
      },
      {
        n: 3,
        title: "The Birth of Jesus",
        verse: "She gave birth to her first-born Son and wrapped Him in swaddling clothes and laid Him in a manger, because there was no room for them in the place where travellers lodged.",
        ref: "Luke 2:7",
        fruit: "Poverty",
      },
      {
        n: 4,
        title: "The Presentation",
        verse: "When the day came to purify them according to the law of Moses, the couple brought Him up to Jerusalem so that He could be presented to the Lord, for it is written in the law of the Lord: 'Every first-born male shall be consecrated to the Lord.'",
        ref: "Luke 2:22–23",
        fruit: "Obedience",
      },
      {
        n: 5,
        title: "The Finding of the Child Jesus in the Temple",
        verse: "On the third day they came upon Him in the temple sitting in the midst of the teachers, listening to them and asking them questions.",
        ref: "Luke 2:46",
        fruit: "Joy in finding Jesus",
      },
    ],
  },
  sorrowful: {
    id: "sorrowful",
    label: "Sorrowful Mysteries",
    day: "Tuesday · Friday",
    kicker: "The Passion",
    color: "from-midnight/20 to-white",
    intro: "The five wounds of the Passion of Christ — the price of our salvation.",
    mysteries: [
      {
        n: 1,
        title: "The Agony in the Garden",
        verse: "In His anguish He prayed with all the greater intensity, and His sweat became like drops of blood falling to the ground. Then He rose from prayer and came to His disciples, only to find them asleep, exhausted with grief.",
        ref: "Luke 22:44–45",
        fruit: "Sorrow for sin",
      },
      {
        n: 2,
        title: "The Scourging at the Pillar",
        verse: "Pilate's next move was to take Jesus and have Him scourged.",
        ref: "John 19:1",
        fruit: "Purity",
      },
      {
        n: 3,
        title: "The Crowning with Thorns",
        verse: "They stripped off His clothes and wrapped Him in a scarlet military cloak. Weaving a crown out of thorns they fixed it on His head, and stuck a reed in His right hand.",
        ref: "Matthew 27:28–29",
        fruit: "Courage",
      },
      {
        n: 4,
        title: "The Carrying of the Cross",
        verse: "Carrying the cross by Himself, He went out to what is called the Place of the Skull (in Hebrew, Golgotha).",
        ref: "John 19:17",
        fruit: "Patience",
      },
      {
        n: 5,
        title: "The Crucifixion",
        verse: "Jesus uttered a loud cry and said, 'Father, into Your hands I commend My spirit.' After He said this, He expired.",
        ref: "Luke 23:46",
        fruit: "Perseverance",
      },
    ],
  },
  glorious: {
    id: "glorious",
    label: "Glorious Mysteries",
    day: "Wednesday · Sunday",
    kicker: "The Resurrection",
    color: "from-gold/25 to-white",
    intro: "The glory of the Risen Christ and the crowning of His Mother.",
    mysteries: [
      {
        n: 1,
        title: "The Resurrection",
        verse: "You need not be amazed! You are looking for Jesus of Nazareth, the one who was crucified. He has been raised up; He is not here. See the place where they laid Him.",
        ref: "Mark 16:6",
        fruit: "Faith",
      },
      {
        n: 2,
        title: "The Ascension",
        verse: "Then, after speaking to them, the Lord Jesus was taken up into Heaven and took His seat at God's right hand.",
        ref: "Mark 16:19",
        fruit: "Hope",
      },
      {
        n: 3,
        title: "The Descent of the Holy Spirit",
        verse: "All were filled with the Holy Spirit. They began to express themselves in foreign tongues and make bold proclamation as the Spirit prompted them.",
        ref: "Acts 2:4",
        fruit: "Love of God",
      },
      {
        n: 4,
        title: "The Assumption",
        verse: "You are the glory of Jerusalem … you are the splendid boast of our people … God is pleased with what you have wrought. May you be blessed by the Lord Almighty forever and ever.",
        ref: "Judith 15:9–10",
        fruit: "Grace of a happy death",
      },
      {
        n: 5,
        title: "The Coronation",
        verse: "A great sign appeared in the sky, a woman clothed with the sun, with the moon under her feet, and on her head a crown of twelve stars.",
        ref: "Revelation 12:1",
        fruit: "Trust in Mary's intercession",
      },
    ],
  },
  luminous: {
    id: "luminous",
    label: "Luminous Mysteries",
    day: "Thursday",
    kicker: "The public ministry",
    color: "from-marian/20 to-white",
    intro: "The mysteries of the light of the world — added by St. John Paul II in 2002.",
    mysteries: [
      {
        n: 1,
        title: "The Baptism of Jesus",
        verse: "And when Jesus was baptised, … the heavens were opened and He saw the Spirit of God descending like a dove, and alighting on Him, and lo, a voice from heaven, saying 'this is My beloved Son,' with whom I am well pleased.",
        ref: "Matthew 3:16–17",
        fruit: "Openness to the Holy Spirit",
      },
      {
        n: 2,
        title: "The Wedding at Cana",
        verse: "His mother said to the servants, 'Do whatever He tells you.' … Jesus said to them, 'Fill the jars with water.' And they filled them up to the brim.",
        ref: "John 2:5–7",
        fruit: "To Jesus through Mary",
      },
      {
        n: 3,
        title: "The Proclamation of the Kingdom",
        verse: "And preach as you go, saying, 'The kingdom of heaven is at hand.' Heal the sick, raise the dead, cleanse lepers, cast out demons. You received without pay, give without pay.",
        ref: "Matthew 10:7–8",
        fruit: "Repentance and trust in God",
      },
      {
        n: 4,
        title: "The Transfiguration",
        verse: "And as He was praying, the appearance of His countenance was altered and His raiment became dazzling white. And a voice came out of the cloud saying, 'This is My Son, My chosen; listen to Him!'",
        ref: "Luke 9:29–35",
        fruit: "Desire for holiness",
      },
      {
        n: 5,
        title: "The Institution of the Eucharist",
        verse: "And He took bread, and when He had given thanks He broke it and gave it to them, saying, 'This is My body which is given for you.' … And likewise the cup after supper, saying, 'This cup which is poured out for you is the new covenant in My blood.'",
        ref: "Luke 22:19–20",
        fruit: "Adoration",
      },
    ],
  },
};

export const ROSARY_SETS = Object.fromEntries(Object.entries(MYSTERY_SETS).map(([id, set]) => [id, { ...set, mysteries: set.mysteries.map((m) => ({ ...m, ...ROSARY_SCRIPTURE[`${id}-${m.n}`] })) }]));

export const SET_BY_WEEKDAY = {
  0: "glorious",   // Sunday
  1: "joyful",     // Monday
  2: "sorrowful",  // Tuesday
  3: "glorious",   // Wednesday
  4: "luminous",   // Thursday
  5: "sorrowful",  // Friday
  6: "joyful",     // Saturday
};

// Prayers of the Rosary — English + a second language column where meaningful.
const BASE_PRAYERS = {
  sign: {
    title: "Sign of the Cross",
    en: "In the name of the Father, and of the Son, and of the Holy Spirit. Amen.",
    la: "In nómine Patris, et Fílii, et Spíritus Sancti. Amen.",
  },
  creed: {
    title: "Apostles' Creed",
    en: "I believe in God, the Father almighty, Creator of heaven and earth, and in Jesus Christ, His only Son, our Lord, who was conceived by the Holy Spirit, born of the Virgin Mary, suffered under Pontius Pilate, was crucified, died and was buried; He descended into hell; on the third day He rose again from the dead; He ascended into heaven, and is seated at the right hand of God the Father almighty; from there He will come to judge the living and the dead. I believe in the Holy Spirit, the holy Catholic Church, the communion of saints, the forgiveness of sins, the resurrection of the body, and life everlasting. Amen.",
    la: "Credo in Deum Patrem omnipoténtem, Creatórem cæli et terræ, et in Iesum Christum, Fílium eius únicum, Dóminum nostrum, qui concéptus est de Spíritu Sancto, natus ex María Vírgine, passus sub Póntio Piláto, crucifíxus, mórtuus, et sepúltus, descéndit ad ínferos, tértia die resurréxit a mórtuis, ascéndit ad cælos, sedet ad déxteram Dei Patris omnipoténtis, inde ventúrus est iudicáre vivos et mórtuos. Credo in Spíritum Sanctum, sanctam Ecclésiam cathólicam, sanctórum communiónem, remissiónem peccatórum, carnis resurrectiónem, vitam ætérnam. Amen.",
  },
  our_father: {
    title: "The Our Father",
    en: "Our Father, who art in heaven, hallowed be Thy name; Thy kingdom come, Thy will be done, on earth as it is in heaven. Give us this day our daily bread; and forgive us our trespasses as we forgive those who trespass against us; and lead us not into temptation, but deliver us from evil. Amen.",
    la: "Pater noster, qui es in cælis, sanctificétur nomen tuum; advéniat regnum tuum; fiat volúntas tua, sicut in cælo, et in terra. Panem nostrum quotidiánum da nobis hódie, et dimítte nobis débita nostra, sicut et nos dimíttimus debitóribus nostris; et ne nos indúcas in tentatiónem; sed líbera nos a malo. Amen.",
  },
  hail_mary: {
    title: "The Hail Mary",
    en: "Hail Mary, full of grace, the Lord is with thee. Blessed art thou among women, and blessed is the fruit of thy womb, Jesus. Holy Mary, Mother of God, pray for us sinners, now and at the hour of our death. Amen.",
    la: "Ave María, grátia plena, Dóminus tecum; benedícta tu in muliéribus, et benedíctus fructus ventris tui, Iesus. Sancta María, Mater Dei, ora pro nobis peccatóribus, nunc et in hora mortis nostræ. Amen.",
  },
  glory: {
    title: "Glory Be",
    en: "Glory be to the Father, and to the Son, and to the Holy Spirit. As it was in the beginning, is now, and ever shall be, world without end. Amen.",
    la: "Glória Patri, et Fílio, et Spirítui Sancto. Sicut erat in princípio, et nunc et semper, et in sǽcula sæculórum. Amen.",
  },
  fatima: {
    title: "The Fátima Prayer",
    en: FATIMA_DECADE_PRAYER,
    la: "O mi Iesu, dimítte nobis peccáta nostra, líbera nos ab igne inférni; condúc omnes ánimas in cælum, præsértim illas quæ máxime indígent misericórdia tua.",
  },
  hail_holy_queen: {
    title: "Hail Holy Queen",
    en: "Hail, holy Queen, Mother of mercy, our life, our sweetness and our hope. To thee do we cry, poor banished children of Eve; to thee do we send up our sighs, mourning and weeping in this valley of tears. Turn then, most gracious advocate, thine eyes of mercy toward us, and after this our exile show unto us the blessed fruit of thy womb, Jesus. O clement, O loving, O sweet Virgin Mary. Pray for us, O holy Mother of God, that we may be made worthy of the promises of Christ. Amen.",
    la: "Salve, Regína, mater misericórdiæ, vita, dulcédo et spes nostra, salve. Ad te clamámus, éxsules fílii Hevæ. Ad te suspirámus, geméntes et flentes in hac lacrimárum valle. Eia ergo, advocáta nostra, illos tuos misericórdes óculos ad nos convérte. Et Iesum, benedíctum fructum ventris tui, nobis post hoc exsílium osténde. O clemens, O pia, O dulcis Virgo María. Ora pro nobis, sancta Dei Génetrix. Ut digni efficiámur promissiónibus Christi. Amen.",
  },
  closing: {
    title: "Closing Prayer",
    en: "O God, whose only-begotten Son, by His life, death and resurrection, has purchased for us the rewards of eternal life; grant, we beseech Thee, that meditating upon these mysteries of the most holy Rosary of the Blessed Virgin Mary, we may imitate what they contain and obtain what they promise, through the same Christ our Lord. Amen.",
    la: "Deus, cuius Unigénitus per vitam, mortem et resurrectiónem suam nobis salútis ætérnæ prǽmia comparávit: concéde, quǽsumus; ut hæc mystéria sacratíssimo beátæ Maríæ Vírginis Rosário recoléntes, et imitémur quod cóntinent, et quod promíttunt, assequámur. Per eúndem Christum Dóminum nostrum. Amen.",
  },
};

export const ROSARY_PRAYERS = Object.fromEntries(Object.entries(BASE_PRAYERS).map(([key, prayer]) => [key, {
  ...prayer, en: TRADITIONAL_ENGLISH[key] || (key === "hail_holy_queen" ? PRAYER_EDITIONS["salve-regina"].english : prayer.en),
  sourceUrl: ["creed", "our_father", "hail_mary"].includes(key) ? BALTIMORE_SOURCE : key === "fatima" ? "https://www.fatima.pt/en/pages/fatima-prayers" : "https://www.vatican.va/special/rosary/documents/misteri_en.html",
  sourceLabel: ["creed", "our_father", "hail_mary"].includes(key) ? "Baltimore Catechism no. 2 · Prayers" : key === "fatima" ? "Fátima shrine · reported prayer (translations vary)" : "Traditional Rosary prayer · Vatican Rosary collection",
  es: SPANISH_PRAYERS[key].text, fr: FRENCH_PRAYERS[key].text,
  titles: { en: prayer.title, la: prayer.title, es: SPANISH_PRAYERS[key].title, fr: FRENCH_PRAYERS[key].title },
}]));

// Physical anchors are language-independent. Chain prayers share the preceding bead;
// the first decade's Our Father is the upper pendant bead, not an extra loop bead.
export const buildSteps = (setId, lang = "en") => {
  const set = localizeSet(ROSARY_SETS[setId], lang);
  const t = rosaryUI(lang);
  const title = (key) => ROSARY_PRAYERS[key].titles[lang] || ROSARY_PRAYERS[key].title;
  const steps = [
    { kind: "prayer", key: "sign", anchor: "crucifix", label: `${t.begin} — ${title("sign")}` },
    { kind: "prayer", key: "creed", anchor: "crucifix", label: title("creed") },
    { kind: "prayer", key: "our_father", anchor: "intro-our-father", label: title("our_father") },
    ...[0, 1, 2].map((i) => ({ kind: "prayer", key: "hail_mary", anchor: `intro-hail-${i + 1}`, label: `${title("hail_mary")} (${i + 1} ${t.of} 3) · ${t.intentions[i]}` })),
    { kind: "prayer", key: "glory", anchor: "intro-glory", label: title("glory") },
  ];
  set.mysteries.forEach((m, i) => {
    const anchor = `decade-our-father-${i}`;
    steps.push({ kind: "mystery", index: i, anchor, label: mysteryLabel(i, lang) });
    steps.push({ kind: "prayer", key: "our_father", index: i, anchor, label: `${title("our_father")} — ${m.title}` });
    for (let h = 1; h <= 10; h++) {
      steps.push({ kind: "bead", index: i, bead: h, anchor: `decade-hail-${i}-${h}`, label: `${title("hail_mary")} ${h} ${t.of} 10 — ${m.title}` });
    }
    steps.push({ kind: "prayer", key: "glory", index: i, anchor: `decade-hail-${i}-10`, label: `${title("glory")} — ${m.title}` });
    steps.push({ kind: "prayer", key: "fatima", index: i, anchor: `decade-hail-${i}-10`, label: `${title("fatima")} — ${m.title}` });
  });
  steps.push({ kind: "prayer", key: "hail_holy_queen", anchor: "medal", label: title("hail_holy_queen") });
  steps.push({ kind: "prayer", key: "closing", anchor: "medal", label: title("closing") });
  steps.push({ kind: "prayer", key: "sign", anchor: "crucifix", label: title("sign") });
  return steps;
};

export const ROSARY_LANGUAGES = [
  { id: "en", label: "English" },
  { id: "la", label: "Latin" },
  { id: "es", label: "Español" },
  { id: "fr", label: "Français" },
];
